class JwsShopRange extends HTMLElement {
  connectedCallback() {
    if (this.abortController) return;
    this.tabs = [...this.querySelectorAll('[data-range-tab]')];
    this.panels = [...this.querySelectorAll('[data-range-panel]')];
    if (!this.panels.length) return;
    this.abortController = new AbortController();
    const { signal } = this.abortController;
    const tablist = this.querySelector('[data-range-tabs]');
    this.hasTabs = Boolean(tablist && this.tabs.length > 1 && this.tabs.length === this.panels.length);
    if (this.hasTabs) {
      tablist.setAttribute('role', 'tablist');
      tablist.setAttribute('aria-orientation', 'horizontal');

      this.tabs.forEach((tab, index) => {
        const panel = this.panels[index];
        tab.setAttribute('role', 'tab');
        tab.setAttribute('aria-controls', panel.id);
        panel.setAttribute('role', 'tabpanel');
        panel.setAttribute('aria-labelledby', tab.id);
        panel.tabIndex = 0;
        tab.addEventListener('click', (event) => {
          event.preventDefault();
          this.selectTab(index);
        }, { signal });
        tab.addEventListener('keydown', (event) => {
          const rtl = getComputedStyle(tablist).direction === 'rtl';
          const nextKey = rtl ? 'ArrowLeft' : 'ArrowRight';
          const previousKey = rtl ? 'ArrowRight' : 'ArrowLeft';
          let nextIndex;
          if (event.key === nextKey) nextIndex = (index + 1) % this.tabs.length;
          else if (event.key === previousKey) nextIndex = (index + this.tabs.length - 1) % this.tabs.length;
          else if (event.key === 'Home') nextIndex = 0;
          else if (event.key === 'End') nextIndex = this.tabs.length - 1;
          else if (event.key === ' ') nextIndex = index;
          else return;
          event.preventDefault();
          this.selectTab(nextIndex, true);
        }, { signal });
      });
    }

    this.rails = this.panels.map((panel) => {
      const rail = panel.querySelector('[data-range-rail]');
      const controls = panel.querySelector('[data-rail-controls]');
      const previous = panel.querySelector('[data-rail-previous]');
      const next = panel.querySelector('[data-rail-next]');
      const entry = { panel, rail, controls, previous, next };
      if (!rail || !controls || !previous || !next) return entry;
      previous.addEventListener('click', () => this.scrollRail(entry, -1), { signal });
      next.addEventListener('click', () => this.scrollRail(entry, 1), { signal });
      rail.addEventListener('scroll', () => this.updateRail(entry), { signal, passive: true });
      rail.addEventListener('load', () => this.updateRail(entry), { signal, capture: true });
      return entry;
    });

    this.setAttribute('data-enhanced', '');
    if (this.hasTabs) {
      const hashIndex = this.panels.findIndex((panel) => `#${panel.id}` === window.location.hash);
      this.selectTab(hashIndex < 0 ? 0 : hashIndex);
    } else {
      this.panels.forEach((panel) => { panel.hidden = false; });
      this.updateRails();
    }
    if ('ResizeObserver' in window) {
      this.resizeObserver = new ResizeObserver(() => this.updateRails());
      this.resizeObserver.observe(this);
    } else {
      window.addEventListener('resize', () => this.updateRails(), { signal, passive: true });
    }
  }

  disconnectedCallback() {
    this.abortController?.abort();
    this.resizeObserver?.disconnect();
    this.abortController = null;
    this.resizeObserver = null;
  }

  selectTab(index, moveFocus = false) {
    this.tabs.forEach((tab, tabIndex) => {
      const selected = tabIndex === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      this.panels[tabIndex].hidden = !selected;
    });
    if (moveFocus) this.tabs[index].focus();
    this.updateRails();
  }

  scrollRail(entry, direction) {
    if (!entry.rail) return;
    const firstCard = entry.rail.firstElementChild;
    const gap = parseFloat(getComputedStyle(entry.rail).columnGap) || 0;
    const step = firstCard ? firstCard.getBoundingClientRect().width + gap : entry.rail.clientWidth;
    const rtl = getComputedStyle(entry.rail).direction === 'rtl';
    const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    entry.rail.scrollBy({ left: step * direction * (rtl ? -1 : 1), behavior: reducedMotion ? 'auto' : 'smooth' });
  }

  updateRail({ panel, rail, controls, previous, next }) {
    if (!rail || !controls || !previous || !next || panel.hidden) return;
    const maximum = Math.max(0, rail.scrollWidth - rail.clientWidth);
    const position = Math.max(0, Math.min(Math.abs(rail.scrollLeft), maximum));
    controls.hidden = maximum <= 2;
    previous.disabled = position <= 2;
    next.disabled = position >= maximum - 2;
  }

  updateRails() {
    this.rails?.forEach((entry) => this.updateRail(entry));
  }
}

if (!customElements.get('jws-shop-range')) customElements.define('jws-shop-range', JwsShopRange);
